import type React from 'react';
import { useEffect, useMemo } from 'react';

import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import {
  type CollectionTitleObject,
  selectCollectionTitleObjects,
} from '../../ducks/collections/selectors';
import { getCollectionParents } from '../../ducks/collections/thunks';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import CollectionRelationsTable, {
  type CollectionRelation,
} from './collection_relations_table/collection_relations_table';

import styles from './asset_relations.module.css';

type Props = {
  deletedItems?: string[];
  collections?: SearchItemEntity[];
  sourceId: string;
  newInCollectionsIds?: string[];
};

const CollectionRelations: React.FC<Props> = ({
  deletedItems = [],
  collections = [],
  sourceId,
  newInCollectionsIds,
}) => {
  const dispatch = useAppDispatch();
  const items = useMemo(
    () => collections.filter((item) => !deletedItems.includes(item.id)),
    [collections, deletedItems],
  );
  // Key based on item ids to only trigger useEffect when necessary
  const itemsKey = useMemo(
    () => items.map((item) => item.id).join(','),
    [items],
  );
  const parentCollectionIds = items.flatMap((item) => item.parents ?? []);
  const collectionTitles = useAppSelector((state) =>
    selectCollectionTitleObjects(state, parentCollectionIds),
  );
  const collectionTitleMap = new Map(
    collectionTitles.map((item) => [item.id, item]),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Keyed based on item ids
  useEffect(() => {
    // We don't really need to wait for this and use the response in any way since it will be added to the entities
    // automatically and the selector above will pick it up
    for (const item of items) {
      dispatch(getCollectionParents(item.id));
    }
  }, [itemsKey]);

  if (!items.length) {
    return null;
  }

  const relationItems: CollectionRelation[] = items.map((item) => {
    const parents = item.parents ?? [];
    const parentTitlesWithId = parents
      .map((id) => collectionTitleMap.get(id))
      .filter(
        (parent): parent is CollectionTitleObject => parent !== undefined,
      );

    const breadcrumbs = [
      ...parentTitlesWithId,
      { id: item.id, title: item.title },
    ];
    return {
      ...item,
      breadcrumbs,
    };
  });

  const title = 'In collections';
  return (
    <div>
      <h3 className={styles.title}>{title}</h3>
      <CollectionRelationsTable
        items={relationItems}
        sourceId={sourceId}
        newInCollectionsIds={newInCollectionsIds}
        label={title}
      />
    </div>
  );
};

export default CollectionRelations;
